import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteHeader } from "../components/layout/SiteHeader";
import { SiteFooter } from "../components/layout/SiteFooter";
import { Helmet } from "react-helmet-async";
import { logEvent } from "../utils/analytics";
import { createPublicSimulation } from "../api/simulation";
import SimulationResults from "../components/console/SimulationResults";
import EmptyArenaPreview from "../components/console/models/EmptyArenaPreview";
import { inputCls, btnAdd, btnDel, thCls } from "../components/console/SimulationForm";
import { FaqAccordion } from "../components/shared/FaqAccordion";
import { ContainerIcon } from "../components/shared/ContainerIcon";
import { useAuth } from "../auth/AuthContext";

const MAX_CONTAINERS = 2;
const MAX_ITEMS = 2;

// Weight = payload capacity = Max Gross Weight (a regulatory ceiling per ISO
// 668) minus tare weight (the container's own weight, which grows with size).
// 20ft is capped at a lower 24t MGW class than 40ft's 30.48t — not a size
// thing, a historical axle-load/crane-handling limit. Within the same length
// class, High Cube carries slightly LESS payload than Standard (more height
// = more steel = more tare, against the same MGW ceiling). 45ft HC is highest
// because it's rated to its own higher ~32.5t MGW class. Verified against a
// single consistent ISO 668 source (see chat) rather than mixed sources.
const STANDARD_CONTAINERS = [
  { name: "20 ft Standard",  lengthFt: 20, highCube: false, length: 5.9,   width: 2.35, height: 2.39, weight: 21700, maxCapacity: { weight: 100, volume: 100 }, cost: 4200 },
  { name: "40 ft Standard",  lengthFt: 40, highCube: false, length: 12.03, width: 2.35, height: 2.39, weight: 26730, maxCapacity: { weight: 100, volume: 100 }, cost: 5800 },
  { name: "20 ft High Cube", lengthFt: 20, highCube: true,  length: 5.9,   width: 2.35, height: 2.7,  weight: 21600, maxCapacity: { weight: 100, volume: 100 }, cost: 4600 },
  { name: "40 ft High Cube", lengthFt: 40, highCube: true,  length: 12.03, width: 2.35, height: 2.7,  weight: 26480, maxCapacity: { weight: 100, volume: 100 }, cost: 6200 },
  { name: "45 ft High Cube", lengthFt: 45, highCube: true,  length: 13.55, width: 2.35, height: 2.7,  weight: 27700, maxCapacity: { weight: 100, volume: 100 }, cost: 6800 },
];

const INITIAL_PACKAGES = [];

// One picker per slot, both always visible side by side — no sequencing,
// no "Add Container" trigger, no cap-tooltip. Not interacting with a slot
// is how you skip it (only slot 1 is effectively required to run anything).
// "Add your own" sits as a flat inline button beside the heading, not buried
// as the last card in the scrollable row.
function ContainerPicker({ onPick, onCustom, slotLabel, takenName }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-bold text-white">{slotLabel}</p>
        <button
          onClick={onCustom}
          className="flex shrink-0 items-center gap-1 rounded-lg border border-white/10 px-2.5 py-1 text-xs font-semibold text-slate-300 transition hover:border-primary/60 hover:text-white"
        >
          <span className="material-symbols-outlined text-sm">tune</span>
          Add your own
        </button>
      </div>
      <div className="mt-3 flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1">
        {STANDARD_CONTAINERS.map((c) => {
          const taken = c.name === takenName;
          return (
            <button
              key={c.name}
              onClick={() => !taken && onPick(c)}
              disabled={taken}
              className={`flex w-[124px] shrink-0 flex-col items-center gap-1 rounded-xl border border-white/10 bg-white/5 px-2 py-2 transition ${
                taken ? "cursor-not-allowed opacity-40" : "hover:border-primary/60 hover:bg-primary/5"
              }`}
            >
              <ContainerIcon lengthFt={c.lengthFt} highCube={c.highCube} />
              <span className="whitespace-nowrap text-center text-xs font-bold text-white">{c.name}</span>
              <span className="whitespace-nowrap text-center text-[9px] text-slate-300">{c.length}×{c.width}×{c.height}m</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// A filled slot — replaces the picker in place once something's been chosen.
// Name is always system-controlled (see setSlotField's custom-conversion
// logic), so it's rendered as text, never an input.
function ContainerSlotCard({ slot, onFieldChange, onReset }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-bold text-white">
          {slot.name}
          <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary text-white" title="Locked in">
            <span className="material-symbols-outlined text-[11px]">check</span>
          </span>
        </p>
        <button onClick={onReset} className="text-slate-500 hover:text-red-400 transition" aria-label="Remove this container">
          <span className="material-symbols-outlined text-base">close</span>
        </button>
      </div>
      <div className="mt-2.5 grid grid-cols-5 gap-1.5">
        {[["length", "L(m)"], ["width", "W(m)"], ["height", "H(m)"], ["weight", "Wt(kg)"], ["cost", "$"]].map(([f, label]) => (
          <label key={f} className="block">
            <span className="block text-center text-[9px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
            <input
              className="mt-0.5 w-full rounded-md border border-white/10 bg-white/5 px-1 py-1 text-center text-xs text-white outline-none transition focus:border-primary/60 focus:bg-white/10"
              type="number"
              step="any"
              value={slot[f]}
              onChange={(e) => onFieldChange(f, e.target.value)}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

// Dimension/weight/qty fields hold raw strings while being edited (so decimal
// entry like "1.5" and clearing the field to retype don't get fought by
// premature parseFloat/parseInt on every keystroke) — coerce to numbers only
// here, at the point they actually leave the form.
const num = (v) => Number(v) || 0;

// Same distance formula EmptyArenaPreview.js uses (totalWidth summed with
// gaps, divided by 2, vs. max length/height, *1.5) computed off the exact
// same source — the picked container slots — so the results 3D view can be
// handed this exact number instead of re-deriving it from the simulation
// result's own data shape (which doesn't necessarily mirror the preview's
// "one shell per picked slot" assumption, e.g. once real quantities are
// involved) and drifting out of sync with what the preview showed.
const ARENA_DIVISOR = 2;
function computeArenaDistance(containers) {
  const complete = containers.filter((c) => num(c.length) > 0 && num(c.width) > 0 && num(c.height) > 0);
  if (!complete.length) return 50;
  const cum = complete.reduce((acc, c, i) => {
    return acc + num(c.width) + (i === complete.length - 1 ? 0 : ARENA_DIVISOR);
  }, 0);
  const totalWidth = cum / ARENA_DIVISOR;
  const maxHeight = Math.max(0, ...complete.map((c) => num(c.height) / ARENA_DIVISOR));
  const maxLength = Math.max(0, ...complete.map((c) => num(c.length) / ARENA_DIVISOR));
  return Math.max(totalWidth, maxLength, maxHeight) * 1.5;
}

function buildPublicPayload(containers, packages) {
  return {
    handlingType: "hand_stuffed",
    measurementUnits: { distance: "meter", weight: "kilogram", currency: "usd", utilisation: "percent" },
    containers: containers.reduce((acc, c) => {
      acc[c.name] = {
        length: num(c.length), width: num(c.width), height: num(c.height),
        weight: { value: num(c.weight), maxUtilisation: c.maxCapacity.weight },
        maxVolumeUtilisation: c.maxCapacity.volume,
        cost: num(c.cost),
      };
      return acc;
    }, {}),
    items: packages.reduce((acc, p) => {
      acc[p.name] = {
        length: num(p.length), width: num(p.width), height: num(p.height), weight: num(p.maxStackingWeight),
        quantity: num(p.quantity), sameStackingOnly: false, maxStackingLevel: num(p.maxStackingCount),
        possibleOrientations: [
          { length: "length", width: "width", height: "height" },
          { length: "width", width: "length", height: "height" },
        ],
      };
      return acc;
    }, {}),
  };
}

export function BeyondCbmPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Fixed 2 slots, always both visible — null means "showing the picker",
  // an object means "filled". No sequencing, no Add Container trigger.
  const [containerSlots, setContainerSlots] = useState([null, null]);
  const [customCounter, setCustomCounter] = useState(0); // monotonic — never decrements, never reused, even if a slot is cleared
  const [packages, setPackages] = useState(INITIAL_PACKAGES);
  const [results, setResults] = useState(null);
  const [view, setView] = useState("form"); // 'form' | 'result' — mirrors the console's own view-swap pattern
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [containerCapLogged, setContainerCapLogged] = useState(false);
  const [itemCapLogged, setItemCapLogged] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);

  const containers = containerSlots.filter(Boolean);
  const atItemCap = packages.length >= MAX_ITEMS;
  const canRun = containers.length > 0 && packages.length > 0;
  // Same number the arena preview computed for these exact picks — handed
  // straight to the results 3D view so it uses the identical camera
  // distance, not a re-derived one.
  const arenaCameraDistance = useMemo(() => computeArenaDistance(containers), [containers]);
  // Cosmetic mask for the share-link tease below — no real shareable link
  // exists yet (createPublicSimulation has no public get-by-id counterpart),
  // so this is a signup lead-gen prompt, not a working link.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shareSlug = useMemo(() => Math.random().toString(36).slice(2, 6), [results]);

  const noteContainerCapHit = () => {
    if (containerCapLogged) return;
    setContainerCapLogged(true);
    logEvent({ name: "beyond_cbm_cap_hit", type: "limit", meta: { which: "container", containers: MAX_CONTAINERS, items: packages.length } });
  };
  const noteItemCapHit = () => {
    if (itemCapLogged) return;
    setItemCapLogged(true);
    logEvent({ name: "beyond_cbm_cap_hit", type: "limit", meta: { which: "item", containers: containers.length, items: packages.length } });
  };

  const pickForSlot = (slotIndex, preset) => {
    setContainerSlots((prev) => {
      const next = [...prev];
      next[slotIndex] = { ...preset, isCustom: false };
      if (next.every(Boolean)) noteContainerCapHit();
      return next;
    });
  };
  const pickCustomForSlot = (slotIndex) => {
    const nextNum = customCounter + 1;
    setCustomCounter(nextNum);
    setContainerSlots((prev) => {
      const next = [...prev];
      next[slotIndex] = {
        name: `Container ${nextNum}`, length: 0, width: 0, height: 0, weight: 0,
        maxCapacity: { weight: 100, volume: 100 }, cost: 0, isCustom: true,
      };
      if (next.every(Boolean)) noteContainerCapHit();
      return next;
    });
  };
  // Editing any field on a still-standard slot silently and permanently
  // converts it to "Custom N" — one-way, never reverts even if the values
  // happen to match a standard container again afterward.
  const setSlotField = (slotIndex, field, value) => {
    const current = containerSlots[slotIndex];
    if (!current.isCustom) {
      const nextNum = customCounter + 1;
      setCustomCounter(nextNum);
      setContainerSlots((prev) => {
        const next = [...prev];
        next[slotIndex] = { ...next[slotIndex], [field]: value, isCustom: true, name: `Container ${nextNum}` };
        return next;
      });
    } else {
      setContainerSlots((prev) => {
        const next = [...prev];
        next[slotIndex] = { ...next[slotIndex], [field]: value };
        return next;
      });
    }
  };
  const resetSlot = (slotIndex) => {
    setContainerSlots((prev) => {
      const next = [...prev];
      next[slotIndex] = null;
      return next;
    });
  };

  const setPackageField = (i, field, value) => setPackages((prev) => {
    const arr = [...prev]; arr[i] = { ...arr[i], [field]: value }; return arr;
  });
  const addPackage = () => {
    if (atItemCap) { noteItemCapHit(); return; }
    setPackages((prev) => [...prev, { name: `Package ${prev.length + 1}`, length: "", width: "", height: "", maxStackingWeight: "", maxStackingCount: "", quantity: "" }]);
  };
  const removePackage = (i) => setPackages((prev) => prev.filter((_, idx) => idx !== i));

  const handleRun = async () => {
    setIsLoading(true);
    setError("");
    try {
      const data = await createPublicSimulation(buildPublicPayload(containers, packages));
      setResults(data);
      setView("result");
      // Swapping view state doesn't change the URL, so React Router's own
      // scroll-restoration never fires here — without this, running from
      // near the bottom of a long input form leaves the results rendered
      // off-screen below the fold.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      logEvent({
        name: "beyond_cbm_simulated",
        type: "tool_use",
        meta: { containers: containers.length, items: packages.length },
      });
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Logged-in users skip the capped free-tool run entirely — this hands the
  // exact same picks off to console's full (uncapped, saved, quota-metered)
  // form instead, via the same sessionStorage + initialData mechanism
  // SimulationsTab.js already uses for forking a saved simulation. Beyond
  // CBM's container/package shape is missing a couple of fields
  // (refrigerated, orientationRestricted) that only exist in the console
  // form — filled in with sensible defaults here, not asked of the user.
  const goToConsole = () => {
    const consoleForm = {
      palletization: false,
      samePackageStackingOnly: false,
      containers: containers.map((c) => ({
        name: c.name,
        length: num(c.length), width: num(c.width), height: num(c.height),
        weight: num(c.weight),
        maxCapacity: { weight: c.maxCapacity.weight, volume: c.maxCapacity.volume },
        cost: num(c.cost),
        refrigerated: false,
      })),
      pallets: [],
      packages: packages.map((p) => ({
        name: p.name,
        length: num(p.length), width: num(p.width), height: num(p.height),
        maxStackingWeight: num(p.maxStackingWeight), maxStackingCount: num(p.maxStackingCount),
        orientationRestricted: false, refrigerated: false,
        quantity: num(p.quantity),
      })),
    };
    sessionStorage.setItem("beyond_cbm_prefill", JSON.stringify(consoleForm));
    logEvent({
      name: "beyond_cbm_run_in_console",
      type: "tool_use",
      meta: { containers: containers.length, items: packages.length },
    });
    navigate("/console");
  };

  // Plain edit/add/remove rows for both sections — no standard-container
  // type picker here (that's ContainerPicker, used on the main input page;
  // pulling it into this panel too meant re-showing the whole "pick a 20ft/
  // 40ft/etc card" selector just to add a second container, which isn't
  // "plain"). Adding a container here always creates a blank custom one,
  // exactly like "Add Package" creates a blank package row.
  //
  // Containers use a plain name-label + <input> row here rather than
  // <table><td> like packages below, because a container's name field is
  // never itself editable (see setSlotField) — nothing to conflict with, so
  // the earlier inputCls/w-full class fight (which only bit the package name
  // <input>) doesn't apply here.
  const editInputsPanel = (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Containers</p>
        {/* Table, not flex+grid — a flex-1 grid stretched to fill this wide
            panel meant 3 number inputs inflated to split the entire leftover
            width between them. A <table> with fixed-width <td>s (same as the
            Packages table below) keeps each field sized to its content
            instead of the panel. */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            {containerSlots.some(Boolean) && (
              <thead><tr>
                {["Name", "Length (m)", "Width (m)", "Height (m)", "Weight (kg)", "Cost ($)", ""].map((h) => <th key={h} className={thCls}>{h}</th>)}
              </tr></thead>
            )}
            <tbody>
              {containerSlots.map((slot, slotIndex) => (
                <tr key={slotIndex} className="border-t border-white/5">
                  {slot ? (
                    <>
                      <td className="py-2 pr-3 w-20 truncate text-xs font-bold text-white" title={slot.name}>{slot.name}</td>
                      {[["length", "w-20"], ["width", "w-20"], ["height", "w-20"], ["weight", "w-20"], ["cost", "w-24"]].map(([f, w]) => (
                        <td key={f} className={`py-2 pr-3 ${w}`}>
                          <input className={inputCls} type="number" step="any" value={slot[f]} onChange={(e) => setSlotField(slotIndex, f, e.target.value)} />
                        </td>
                      ))}
                      <td className="py-2">
                        <button className={btnDel} onClick={() => resetSlot(slotIndex)} aria-label="Remove this container"><span className="material-symbols-outlined text-sm">close</span></button>
                      </td>
                    </>
                  ) : (
                    <td colSpan={7} className="py-2">
                      <button onClick={() => pickCustomForSlot(slotIndex)} className={`${btnAdd} w-full justify-center`}>
                        <span className="material-symbols-outlined text-base">add</span> Add container
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Packages</p>
        <div className="overflow-x-auto">
          {/* table-layout:fixed + <colgroup> + width:100%, no min-w-full —
              verified live: min-w-full (min-width, not width) forced the
              table wider than the colgroup's own declared sum without an
              explicit width to anchor the distribution algorithm, so the
              browser fell back to content-driven sizing per column, which is
              what squeezed Name/Qty. An explicit width:100% makes the browser
              distribute all available panel width proportionally to the
              colgroup ratios instead — fills the panel with no leftover gap,
              and still scrolls via overflow-x-auto if the panel is ever
              narrower than the columns' minimums. */}
          <table style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '4.75rem' }} />
              <col style={{ width: '5.975rem' }} />
              <col style={{ width: '4.275rem' }} />
              <col style={{ width: '4.275rem' }} />
              <col style={{ width: '4.275rem' }} />
              <col style={{ width: '4.275rem' }} />
              <col style={{ width: '4.275rem' }} />
              <col style={{ width: '1.9rem' }} />
            </colgroup>
            {packages.length > 0 && (
              <thead><tr>
                {["Name", "Qty", "L (m)", "W (m)", "H (m)", "Wt (kg)", "Stack", ""].map((h) => <th key={h} className={`${thCls} truncate`}>{h}</th>)}
              </tr></thead>
            )}
            <tbody>
              {packages.map((pkg, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-2 pr-3 truncate text-xs font-bold text-white" title={pkg.name}>{pkg.name}</td>
                  <td className="py-2 pr-3"><input className={inputCls} type="number" step="any" value={pkg.quantity} onChange={(e) => setPackageField(i, "quantity", e.target.value)} /></td>
                  {["length", "width", "height", "maxStackingWeight", "maxStackingCount"].map((f) => (
                    <td key={f} className="py-2 pr-3"><input className={inputCls} type="number" step="any" value={pkg[f]} onChange={(e) => setPackageField(i, f, e.target.value)} /></td>
                  ))}
                  <td className="py-2">
                    <button className={btnDel} onClick={() => removePackage(i)}><span className="material-symbols-outlined text-sm">close</span></button>
                  </td>
                </tr>
              ))}
              {!atItemCap && (
                <tr className="border-t border-white/5">
                  <td colSpan={8} className="py-2">
                    <button onClick={addPackage} className={`${btnAdd} w-full justify-center`}>
                      <span className="material-symbols-outlined text-base">add</span> Add package
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          <span>{error}</span>
          <button onClick={() => setError("")} className="shrink-0 text-red-300/70 hover:text-red-200 transition" aria-label="Dismiss error">
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      )}

      <button
        disabled={isLoading || !canRun}
        onClick={async () => {
          // Only close on success — closing unconditionally meant a failed
          // rerun's error banner vanished along with the panel, so the user
          // just saw nothing happen with no indication why.
          const ok = await handleRun();
          if (ok) setEditPanelOpen(false);
        }}
        className="w-full rounded-lg bg-gradient-to-r from-primary to-blue-500 py-2 text-sm font-bold text-white transition hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? "Running…" : "Run updated simulation"}
      </button>
    </div>
  );

  // Tease, not a real link — matches the "Sign up free" hover-flip badges
  // used for the container/item caps above. Clicking or hovering never
  // reveals a working URL; it's a conversion prompt, tracked separately from
  // the cap-hit CTAs so we can tell which nudge actually drives signups.
  // Both pill states are absolutely stacked inside a fixed-size box (h-8,
  // min-w) and crossfaded with opacity, instead of a hidden/flex display
  // toggle — a display swap changes the element's own layout size on hover,
  // and since this sits directly above the 3D canvas, that reflow visibly
  // nudged the canvas by a sub-pixel amount on every hover.
  const shareCta = (
    <Link
      to="/login?mode=signup"
      className="group relative flex h-8 min-w-[230px] shrink-0 items-center justify-center"
      onClick={() => logEvent({ name: "beyond_cbm_share_cta", type: "cta", meta: {} })}
    >
      <span className="absolute inset-0 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-3 text-xs font-semibold text-slate-400 transition-opacity group-hover:opacity-0">
        <span className="material-symbols-outlined text-sm">link</span>
        feuteuristix.com/s/••••{shareSlug}
      </span>
      <span className="absolute inset-0 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-primary/60 bg-primary/15 px-3 text-xs font-bold text-primary opacity-0 transition-opacity group-hover:opacity-100">
        Sign up to save and share
        <span className="material-symbols-outlined text-xs">arrow_forward</span>
      </span>
      <div className="absolute right-0 top-full z-20 w-64 rounded-lg border border-white/10 bg-[#0d1421] p-3 text-xs text-slate-300 opacity-0 shadow-xl transition pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
        Free tool results aren't shareable yet. Sign up free to generate a live link
      </div>
    </Link>
  );

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-dark text-white">
      <Helmet>
        <title>Container Cost Optimization — Free 3D Cargo Load Simulator | Feuteuristix</title>
        <meta name="description" content="Optimize your container shipping cost — find the cheapest container combination and full 3D packing plan, free, no signup needed. Real simulation, not an estimate." />
        <meta property="og:title" content="Container Cost Optimization — Free 3D Cargo Load Simulator | Feuteuristix" />
        <meta property="og:description" content="Optimize your container shipping cost — find the cheapest container combination and full 3D packing plan, free, no signup needed. Real simulation, not an estimate." />
        <meta property="og:url" content="https://feuteuristix.com/3d-cargo-load-simulator" />
        <meta name="twitter:title" content="Container Cost Optimization — Free 3D Cargo Load Simulator | Feuteuristix" />
        <meta name="twitter:description" content="Optimize your container shipping cost — find the cheapest container combination and full 3D packing plan, free, no signup needed. Real simulation, not an estimate." />
        <link rel="canonical" href="https://feuteuristix.com/3d-cargo-load-simulator" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is a 3D cargo load simulator?",
              "acceptedAnswer": { "@type": "Answer", "text": "A 3D cargo load simulator runs the actual container-packing algorithm on your shipment, not just volume math. It calculates the cheapest combination of containers for your cargo and shows exactly how each item is arranged and stacked inside, in three dimensions, accounting for weight limits, stacking rules, and orientation constraints." }
            },
            {
              "@type": "Question",
              "name": "What's the difference between the CBM Calculator and the 3D Load Simulator?",
              "acceptedAnswer": { "@type": "Answer", "text": "The CBM Calculator tells you your shipment's total volume and which single container type it fits in. The 3D Load Simulator goes further: it runs the real bin-packing algorithm across multiple item types and container options simultaneously, finds the cheapest combination — not just the first one that fits — and shows the exact 3D packing arrangement." }
            },
            {
              "@type": "Question",
              "name": "How is the cheapest container combination calculated?",
              "acceptedAnswer": { "@type": "Answer", "text": "The simulator evaluates combinations of container types and quantities against your item dimensions, weights, and quantities, factoring in per-container cost, to find the lowest-cost combination that fits everything — real container cost optimization, not a rule of thumb. Two 20ft containers can cost less than one 40ft, or a 40ft High Cube can outperform two 20ft Standards, depending on your cargo mix." }
            },
            {
              "@type": "Question",
              "name": "Can I simulate mixed cargo — multiple item types in one container?",
              "acceptedAnswer": { "@type": "Answer", "text": "Yes, that's the core difference from simple volume calculators. Real shipments almost always mix SKUs of different sizes, and packing efficiency depends on how well those shapes interlock, not just total volume." }
            },
            {
              "@type": "Question",
              "name": "Does this work for air freight, not just sea freight?",
              "acceptedAnswer": { "@type": "Answer", "text": "Yes. Air freight uses a different volumetric weight ratio — 1 CBM = 167 kg, vs. 1,000 kg for sea freight LCL — and the simulator accounts for this when calculating chargeable weight and cost." }
            },
            {
              "@type": "Question",
              "name": "How accurate is the 3D packing simulation?",
              "acceptedAnswer": { "@type": "Answer", "text": "The simulator uses the same packing engine behind Feuteuristix's full container-load planning platform — weight distribution, stacking limits, item orientation restrictions, and refrigeration/handling constraints, not just geometric volume fitting. This algorithm currently powers container load planning for 2 of India's largest conglomerates." }
            },
            {
              "@type": "Question",
              "name": "Is there a free version, or do I need to sign up?",
              "acceptedAnswer": { "@type": "Answer", "text": "You can run a full, real 3D packing simulation for free right now, no signup — up to 2 containers and 2 item types. Sign up free for the complete planner: unlimited item types, palletization, and 10 simulations a month included." }
            }
          ]
        })}</script>
      </Helmet>
      {/* Header (and footer) stay visible in result view — this is still an
          anonymous/marketing page, not an app shell, so nav should always be
          reachable. Only the hero and FAQ hide once a simulation has run. */}
      <SiteHeader />

      <main className="flex-1 pt-[76.8px] pb-16">
        {view === "form" && (
        <section className="relative bg-gradient-to-b from-background-dark to-slate-950/60 py-14">
          {/* Single placement for this link — top right of the hero, not
              competing with the centered intro copy below it and not
              interrupting reading flow the way a mid-copy link did. */}
          <Link
            to="/examples"
            onClick={() => logEvent({ name: "beyond_cbm_hero_examples_cta", type: "cta" })}
            className="absolute right-6 top-4 hidden items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-400 transition hover:border-primary/40 hover:text-primary sm:flex md:right-8"
          >
            Not sure? See 7 real examples
            <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
          </Link>
          <div className="mx-auto max-w-4xl px-6 md:px-8 text-center">
            <h1 className="text-4xl font-black leading-tight md:text-5xl">
              Beyond CBM
              <span className="mt-2 block text-2xl font-bold text-slate-400 md:text-3xl">Container Cost Optimization, in 3D</span>
            </h1>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
              Go beyond volume math. Run the real packing algorithm to optimize your container cost — find the cheapest combination and see exactly how your cargo fits, in 3D.
            </p>
          </div>
        </section>
        )}

        {/* Tool — starts right after the hero, no separate CTA, no distinct
            "chapter" background of its own — it continues the hero's plain
            background rather than standing apart. Inputs stay at a narrower
            max-w-5xl (easier to scan); results widen to max-w-6xl since the
            console's 3D view looks cramped boxed into an article-width column. */}
        <section className="px-6 md:px-8 py-10">
          {view === "form" && (
          <div className="mx-auto max-w-5xl space-y-6">
          {/* Experimental — the 3D arena, visible before a simulation has
              even been run, filling in with real-sized empty container
              shells as they're picked below. Testing whether showing "this
              produces a 3D result" up front beats only revealing it after
              Run Simulation. */}
          <div id="beyond-cbm-arena" className="scroll-mt-28">
            <EmptyArenaPreview containers={containers} />
          </div>
          {/* Container + Package types — one merged card, not two */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-[22.8px] md:p-[30.4px] space-y-8">
            <div id="container-types" className="scroll-mt-28">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white">Container Types</h3>
                {/* Structural cap (always exactly 2 slots, no "Add" trigger to
                    gate on) — shown as a persistent badge rather than the
                    conditional-on-click pattern Package Types uses below. */}
                <Link to="/login?mode=signup" className="group relative flex" onClick={() => logEvent({ name: "beyond_cbm_cap_badge_cta", type: "cta", meta: { which: "container" } })}>
                  <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400 transition group-hover:hidden">
                    <span className="material-symbols-outlined text-xs">lock</span>
                    {MAX_CONTAINERS} max
                  </span>
                  <span className="hidden items-center gap-1 rounded-full border border-primary/60 bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary transition group-hover:flex">
                    Sign up free
                    <span className="material-symbols-outlined text-xs">arrow_forward</span>
                  </span>
                  <div className="absolute left-0 top-full z-20 w-56 rounded-lg border border-white/10 bg-[#0d1421] p-3 text-xs text-slate-300 opacity-0 shadow-xl transition pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                    Free tool is capped at {MAX_CONTAINERS} containers. Upgrade for unlimited.
                  </div>
                </Link>
              </div>
              {/* Two fixed slots, always both visible — pick both at once,
                  pick one and leave the other empty, or leave both as pickers
                  and run with whatever's filled. No "Add Container" needed. */}
              <div className="mt-4 grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                {containerSlots.map((slot, slotIndex) => (
                  <div key={slotIndex}>
                    {slot ? (
                      <ContainerSlotCard
                        slot={slot}
                        onFieldChange={(field, value) => setSlotField(slotIndex, field, value)}
                        onReset={() => resetSlot(slotIndex)}
                      />
                    ) : (
                      <ContainerPicker
                        onPick={(preset) => pickForSlot(slotIndex, preset)}
                        onCustom={() => pickCustomForSlot(slotIndex)}
                        slotLabel={`Pick your ${slotIndex === 0 ? "first" : "second"} container`}
                        takenName={containerSlots[slotIndex === 0 ? 1 : 0]?.isCustom === false ? containerSlots[slotIndex === 0 ? 1 : 0].name : null}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-8">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-white">Package Types</h3>
                  <Link to="/login?mode=signup" className="group relative flex" onClick={() => logEvent({ name: "beyond_cbm_cap_badge_cta", type: "cta", meta: { which: "item" } })}>
                    <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400 transition group-hover:hidden">
                      <span className="material-symbols-outlined text-xs">lock</span>
                      {MAX_ITEMS} max
                    </span>
                    <span className="hidden items-center gap-1 rounded-full border border-primary/60 bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary transition group-hover:flex">
                      Sign up free
                      <span className="material-symbols-outlined text-xs">arrow_forward</span>
                    </span>
                    <div className="absolute left-0 top-full z-20 w-56 rounded-lg border border-white/10 bg-[#0d1421] p-3 text-xs text-slate-300 opacity-0 shadow-xl transition pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                      Free tool is capped at {MAX_ITEMS} item types. Upgrade for unlimited.
                    </div>
                  </Link>
                </div>
                <div className="relative group">
                  <button
                    onClick={addPackage}
                    className={`${btnAdd} ${atItemCap ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span className="material-symbols-outlined text-base">add</span> Add Package
                  </button>
                  {atItemCap && (
                    <div className="absolute right-0 top-full z-20 w-64 rounded-lg border border-white/10 bg-[#0d1421] p-3 text-xs text-slate-300 opacity-0 shadow-xl transition pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
                      Free tool is capped at {MAX_ITEMS} item types.{" "}
                      <Link to="/login?mode=signup" className="font-bold text-primary hover:underline">Sign up free</Link> for unlimited.
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                {/* table-layout:fixed + <colgroup> + width:100%, not
                    min-w-full auto-layout — with auto layout, a fixed w-XX on
                    each <td> is only ever a hint, and any leftover space
                    beyond what those hints add up to is simply left blank
                    instead of being distributed to the columns (that's the
                    gap on the right). Fixed layout + width:100% makes the
                    browser distribute all available width proportionally to
                    the colgroup ratios, so it always fills the row. */}
                <table style={{ tableLayout: 'fixed', width: '100%' }}>
                  <colgroup>
                    <col style={{ width: '18rem' }} />
                    <col style={{ width: '6rem' }} />
                    <col style={{ width: '5rem' }} />
                    <col style={{ width: '5rem' }} />
                    <col style={{ width: '5rem' }} />
                    <col style={{ width: '5rem' }} />
                    <col style={{ width: '5rem' }} />
                    <col style={{ width: '2rem' }} />
                  </colgroup>
                  {packages.length > 0 && (
                    <thead><tr>
                      {["Name", "Qty", "Length (m)", "Width (m)", "Height (m)", "Weight (kg)", "Max Stack", ""].map((h) => <th key={h} className={thCls}>{h}</th>)}
                    </tr></thead>
                  )}
                  <tbody>
                    {packages.map((pkg, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="py-2 pr-3"><input className={inputCls} value={pkg.name} onChange={(e) => setPackageField(i, "name", e.target.value)} /></td>
                        <td className="py-2 pr-3"><input className={inputCls} type="number" step="any" value={pkg.quantity} onChange={(e) => setPackageField(i, "quantity", e.target.value)} /></td>
                        {["length", "width", "height", "maxStackingWeight", "maxStackingCount"].map((f) => (
                          <td key={f} className="py-2 pr-3"><input className={inputCls} type="number" step="any" value={pkg[f]} onChange={(e) => setPackageField(i, f, e.target.value)} /></td>
                        ))}
                        <td className="py-2">
                          <button className={btnDel} onClick={() => removePackage(i)}><span className="material-symbols-outlined text-sm">close</span></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              <span>{error}</span>
              <button onClick={() => setError("")} className="shrink-0 text-red-300/70 hover:text-red-200 transition" aria-label="Dismiss error">
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>
          )}

          <div className="flex flex-col items-end gap-2">
            {!canRun && !isLoading && (
              <p className="text-xs text-slate-500">Add at least one container and one package to run.</p>
            )}
            <button
              disabled={isLoading || !canRun}
              onClick={isAuthenticated ? goToConsole : handleRun}
              className="h-12 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-10 text-base font-bold text-white shadow-lg shadow-blue-600/30 transition hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAuthenticated ? "Run in console" : isLoading ? "Running…" : "Run Simulation"}
            </button>
          </div>
          </div>
          )}

          {view === "result" && results && (
            <div className="mx-auto max-w-6xl space-y-4">
              <SimulationResults
                results={results}
                inputContainers={containers}
                inputPackages={packages}
                cameraZoom={0.55}
                cameraRotationDeg={10}
                cameraTiltDeg={5}
                cameraDistanceOverride={arenaCameraDistance}
                edgeColor="#0d7ff2"
                canvasHeight={560}
                onBack={() => setView("form")}
                editPanel={editInputsPanel}
                editPanelOpen={editPanelOpen}
                onEditPanelOpenChange={setEditPanelOpen}
                shareCta={shareCta}
                showPackageFilter
                showLegend
              />

              {/* Separated from the results above as its own section — extra
                  top margin plus a divider, rather than sitting flush under
                  the table in the same space-y-4 rhythm. Whole card is now
                  the click target (not just the inner pill), with the same
                  hover-lift + press-scale treatment as the landing page's
                  final CTA block, so it reads as one clickable unit. */}
              <div className="mt-12 border-t border-white/10 pt-8">
                <Link
                  to="/login?mode=signup"
                  onClick={() => logEvent({ name: "beyond_cbm_result_cta", type: "cta" })}
                  className="group relative mx-auto block w-full max-w-[85%] overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-slate-900 via-primary/5 to-slate-900 p-6 transition-all hover:-translate-y-1 hover:border-primary/60 hover:shadow-2xl hover:shadow-primary/30 active:scale-[0.98]"
                >
                  <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-cyan-500/15 blur-3xl" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/5 to-cyan-500/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Like what you see?</p>
                      <p className="mt-1 text-lg font-black text-white leading-snug">
                        That was capped at 2 containers, 2 items.<br className="hidden md:block" />
                        <span className="bg-gradient-to-r from-primary via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                          Sign up for the real planner — free.
                        </span>
                      </p>
                      <p className="mt-2 text-sm text-slate-400 max-w-md">
                        <strong className="text-white">Unlimited item types, palletization, every container type</strong> — plus 10 free simulations a month, no card required.
                      </p>
                    </div>
                    <span className="shrink-0 flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-primary/30 transition group-hover:brightness-110">
                      Unlock full access
                      <span className="material-symbols-outlined text-[16px] transition-transform group-hover:translate-x-1">arrow_forward</span>
                    </span>
                  </div>
                </Link>
                {/* Comforting secondary path — this result was capped at 2/2,
                    so "not ready to commit yet" is a completely reasonable
                    reaction; a plain centered text link (not a second
                    button) offers a no-signup way to see what an unlimited
                    run actually looks like, instead of the only option being
                    "sign up or leave." */}
                <p className="mt-4 text-center text-sm text-slate-500">
                  Not ready to sign up?{" "}
                  <Link
                    to="/examples"
                    onClick={() => logEvent({ name: "beyond_cbm_result_examples_cta", type: "cta" })}
                    className="font-semibold text-slate-300 underline decoration-slate-600 underline-offset-2 transition hover:text-primary hover:decoration-primary"
                  >
                    See what a full-scale run looks like, already packed
                  </Link>
                </p>
              </div>
            </div>
          )}
        </section>

        {/* SEO body content — hidden entirely in result view, per "only show the
            simulation and results" once a simulation has run. */}
        {view === "form" && (
        <>
        <section className="bg-white/[0.025] border-y border-white/5 py-10">
          <div className="mx-auto max-w-4xl px-6 md:px-8">
          <article id="cbm-vs-3d-simulator" className="scroll-mt-28">
            <h2 className="text-2xl font-black text-white mb-4">
              <Link to="/cbm-calculator" className="underline decoration-white/30 underline-offset-4 hover:text-primary hover:decoration-primary transition">CBM Calculator</Link> vs. 3D Load Simulator — what's the difference?
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-white/10">
                    {["", "CBM Calculator", "3D Load Simulator"].map((h) => (
                      <th key={h} className="pb-3 pr-6 text-xs font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  {[
                    ["What it tells you", "Total volume, which single container fits", "Cost-optimized container combination, exact packing arrangement"],
                    ["Item types", "Volume-only math", "Real bin-packing across multiple item types together"],
                    ["Output", "A number + container size", "A full 3D packing plan you can see"],
                    ["Signup", "Never required", "Free to try (2 containers, 2 items) — sign up for full scale"],
                  ].map(([label, ...rest]) => (
                    <tr key={label} className="border-b border-white/5">
                      <td className="py-3 pr-6 font-semibold text-white">{label}</td>
                      {rest.map((v, i) => <td key={i} className="py-3 pr-6">{v}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
          </div>
        </section>

        {/* FAQ — distinct, slightly darker section background, same treatment as
            the CBM Calculator page so both read consistently. */}
        <section className="bg-black/20 border-y border-white/5 py-12">
          <div className="mx-auto max-w-4xl px-6 md:px-8">
            <h2 className="text-xl font-black text-white mb-5">Frequently asked questions</h2>
            <FaqAccordion
              items={[
                {
                  id: "what-is-3d-simulator",
                  question: "What is a 3D cargo load simulator?",
                  answer: (
                    <p>
                      A 3D cargo load simulator runs the actual container-packing algorithm on your shipment — not just volume math. It calculates the <strong className="text-white">cheapest combination of containers</strong> for your cargo and shows exactly how each item is arranged and stacked inside, in three dimensions, accounting for weight limits, stacking rules, and orientation constraints.
                    </p>
                  ),
                },
                {
                  id: "cheapest-combination",
                  question: "How is the cheapest container combination calculated?",
                  answer: (
                    <p>
                      The simulator evaluates combinations of container types and quantities against your item dimensions, weights, and quantities, factoring in per-container cost, to find the lowest-cost combination that fits everything — real container cost optimization, not a rule of thumb. Two 20ft containers can cost less than one 40ft, or a 40ft High Cube can outperform two 20ft Standards — it depends entirely on your cargo mix.
                    </p>
                  ),
                },
                {
                  id: "mixed-cargo",
                  question: "Can I simulate mixed cargo — multiple item types in one container?",
                  answer: (
                    <p>
                      Yes — that's the core difference from simple volume calculators. Real shipments almost always mix SKUs of different sizes, and packing efficiency depends on how well those shapes interlock, not just total volume.
                    </p>
                  ),
                },
                {
                  id: "air-freight",
                  question: "Does this work for air freight, not just sea freight?",
                  answer: (
                    <p>
                      Yes. Air freight uses a different volumetric weight ratio — <strong className="text-white">1 CBM = 167 kg</strong>, vs. 1,000 kg for sea freight LCL — and the simulator accounts for this when calculating chargeable weight and cost.
                    </p>
                  ),
                },
                {
                  id: "accuracy",
                  question: "How accurate is the 3D packing simulation?",
                  answer: (
                    <>
                      <p>
                        The simulator uses the same packing engine behind Feuteuristix's full container-load planning platform — weight distribution, stacking limits, item orientation restrictions, and refrigeration/handling constraints, not just geometric volume fitting. This algorithm currently powers container load planning for 2 of India's largest conglomerates.<sup>*</sup>
                      </p>
                      <p className="mt-2 text-xs text-slate-500">
                        *Used indirectly through a logistics technology integration partner.
                      </p>
                    </>
                  ),
                },
                {
                  id: "free-tier",
                  question: "Is there a free version, or do I need to sign up?",
                  answer: (
                    <p>
                      You can run a full, real 3D packing simulation for free right now, no signup — up to 2 containers and 2 item types. Sign up free for the complete planner: unlimited item types, palletization, and 10 simulations a month included.
                    </p>
                  ),
                },
              ]}
            />
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="mx-auto max-w-4xl px-6 md:px-8 pt-14 pb-8">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-lg font-black text-white">Ready for the full picture?</p>
              <p className="mt-1 text-sm text-slate-400 max-w-md">
                This free tool caps at 2 containers and 2 items. Sign up for unlimited items, palletization, every container type, and 10 simulations a month — free.
              </p>
            </div>
            <Link
              to="/login?mode=signup"
              onClick={() => logEvent({ name: "beyond_cbm_bottom_cta", type: "cta" })}
              className="shrink-0 flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-6 py-3 text-sm font-bold text-white transition hover:brightness-110"
            >
              Sign up free
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </Link>
          </div>
        </section>
        </>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
